import { useEffect, useState } from "react";
import { Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";

import api, { formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

const groups = [
    {
        title: "Company",
        fields: [
            { name: "company_name", label: "Company name" },
            { name: "contact_email", label: "Contact email", type: "email" },
            { name: "contact_phone", label: "Contact phone" },
            { name: "whatsapp_number", label: "WhatsApp number (with country code)" },
            { name: "business_address", label: "Business address", type: "textarea", rows: 2 },
            {
                name: "whatsapp_default_message",
                label: "WhatsApp default message",
                type: "textarea",
                rows: 3,
            },
        ],
    },
    {
        title: "Social",
        fields: [
            { name: "instagram_url", label: "Instagram URL" },
            { name: "facebook_url", label: "Facebook URL" },
            { name: "linkedin_url", label: "LinkedIn URL" },
        ],
    },
    {
        title: "Tracking & SEO",
        fields: [
            { name: "google_analytics_id", label: "Google Analytics ID" },
            {
                name: "google_search_console_verification",
                label: "Google Search Console verification token",
            },
            { name: "microsoft_clarity_id", label: "Microsoft Clarity ID" },
            { name: "facebook_pixel_id", label: "Facebook Pixel ID" },
        ],
    },
    {
        title: "Homepage hero stats",
        fields: [
            { name: "hero_stat_1_value", label: "Stat 1 value (e.g. 40+)" },
            { name: "hero_stat_1_label", label: "Stat 1 label" },
            { name: "hero_stat_2_value", label: "Stat 2 value" },
            { name: "hero_stat_2_label", label: "Stat 2 label" },
            { name: "hero_stat_3_value", label: "Stat 3 value" },
            { name: "hero_stat_3_label", label: "Stat 3 label" },
        ],
    },
];

export default function SettingsPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [emailStatus, setEmailStatus] = useState({ resend_configured: false });
    const [sendingTest, setSendingTest] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/site-settings");
                setData(data);
            } catch (err) {
                toast.error(formatError(err));
            } finally {
                setLoading(false);
            }
        })();
        api.get("/admin/email-status")
            .then(({ data }) => setEmailStatus(data))
            .catch(() => {});
    }, []);

    const update = (name, value) => setData((p) => ({ ...p, [name]: value }));

    const sendTest = async () => {
        if (!user?.email) {
            toast.error("Could not determine your admin email.");
            return;
        }
        setSendingTest(true);
        try {
            const { data } = await api.post("/admin/test-email", {
                to: user.email,
                kind: "admin",
            });
            toast.success(`Test email sent to ${user.email}`);
            if (data?.resend_message_id) console.info("Resend message id:", data.resend_message_id);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSendingTest(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...data };
            delete payload.id;
            delete payload.updated_at;
            const { data: saved } = await api.put("/admin/site-settings", payload);
            setData(saved);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data)
        return (
            <div className="grid place-items-center py-20 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );

    return (
        <form onSubmit={save} className="space-y-8 max-w-3xl" data-testid="settings-page">
            <header>
                <div className="label-eyebrow">Configure</div>
                <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-slate-950 mt-1">
                    Site Settings
                </h1>
                <p className="text-sm text-slate-500 mt-2">
                    Company contact info, social links and tracking pixels shown on the
                    public site.
                </p>
            </header>

            {groups.map((g, gi) => (
                <section key={g.title} className="space-y-4">
                    {gi > 0 && <Separator />}
                    <h2 className="font-display text-lg font-bold text-slate-950">
                        {g.title}
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {g.fields.map((f) => (
                            <div
                                key={f.name}
                                className={`space-y-2 ${
                                    f.type === "textarea" ? "sm:col-span-2" : ""
                                }`}
                            >
                                <Label htmlFor={f.name}>{f.label}</Label>
                                {f.type === "textarea" ? (
                                    <Textarea
                                        id={f.name}
                                        value={data[f.name] || ""}
                                        onChange={(e) => update(f.name, e.target.value)}
                                        rows={f.rows || 3}
                                        data-testid={`settings-${f.name}`}
                                    />
                                ) : (
                                    <Input
                                        id={f.name}
                                        type={f.type || "text"}
                                        value={data[f.name] || ""}
                                        onChange={(e) => update(f.name, e.target.value)}
                                        data-testid={`settings-${f.name}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ))}

            <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-slate-200">
                {emailStatus.resend_configured ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={sendTest}
                        disabled={sendingTest}
                        className="border-slate-300"
                        data-testid="settings-send-test-email"
                    >
                        {sendingTest ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" /> Send test email
                            </>
                        )}
                    </Button>
                ) : (
                    <p className="text-xs text-slate-500 self-center">
                        Resend not configured — set <code>RESEND_API_KEY</code> in backend env to enable test emails.
                    </p>
                )}
                <Button
                    type="submit"
                    disabled={saving}
                    className="bg-slate-950 hover:bg-slate-800 text-white"
                    data-testid="settings-save-button"
                >
                    {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" /> Save settings
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
}
